// ============================================
// Chat Module - Context Menu (ES6)
// 메시지 컨텍스트 메뉴 (Long Press)
// 이벤트 위임 패턴으로 메모리 누수 방지
// ============================================

import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentRoomId, getCurrentSessionId, setCurrentTurnCount, updateFixedMetaBar } from '../app/state.js';
import { renderMemoryPanel } from '../memory.js';
import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';

// ============================================
// 상태 변수
// ============================================
let msgLongPressTimer = null;
let contextMenuTarget = null;
let contextMenuContent = null;
let longPressStartX = 0;
let longPressStartY = 0;
let isLongPressListenerSetup = false;

const MSG_LONG_PRESS_DURATION = 500; // 0.5초
const MOVE_THRESHOLD = 10;

// ============================================
// Long Press 이벤트 위임 설정 (한 번만 호출)
// ============================================

export function initMessageLongPress() {
  if (isLongPressListenerSetup) return;

  const container = document.getElementById('messagesContainer');
  if (!container) return;

  // 터치 이벤트 (모바일)
  container.addEventListener('touchstart', handleTouchStart, { passive: false });
  container.addEventListener('touchmove', handleTouchMove, { passive: true });
  container.addEventListener('touchend', handleTouchEnd, { passive: true });
  container.addEventListener('touchcancel', handleTouchEnd, { passive: true });

  // 마우스 이벤트 (데스크톱)
  container.addEventListener('mousedown', handleMouseDown);
  container.addEventListener('mousemove', handleMouseMove);
  container.addEventListener('mouseup', handleMouseUp);
  container.addEventListener('mouseleave', handleMouseUp);

  isLongPressListenerSetup = true;
}

// ============================================
// 터치 핸들러
// ============================================

function handleTouchStart(e) {
  const message = e.target.closest('.message');
  if (!message) return;

  longPressStartX = e.touches[0].clientX;
  longPressStartY = e.touches[0].clientY;

  const content = message.dataset.rawContent || '';

  msgLongPressTimer = setTimeout(() => {
    e.preventDefault();
    showMessageContextMenu(message, content);
  }, MSG_LONG_PRESS_DURATION);
}

function handleTouchMove(e) {
  if (!msgLongPressTimer) return;

  const moveX = Math.abs(e.touches[0].clientX - longPressStartX);
  const moveY = Math.abs(e.touches[0].clientY - longPressStartY);

  if (moveX > MOVE_THRESHOLD || moveY > MOVE_THRESHOLD) {
    clearTimeout(msgLongPressTimer);
    msgLongPressTimer = null;
  }
}

function handleTouchEnd() {
  if (msgLongPressTimer) {
    clearTimeout(msgLongPressTimer);
    msgLongPressTimer = null;
  }
}

// ============================================
// 마우스 핸들러
// ============================================

function handleMouseDown(e) {
  if (e.button !== 0) return;

  const message = e.target.closest('.message');
  if (!message) return;

  longPressStartX = e.clientX;
  longPressStartY = e.clientY;

  const content = message.dataset.rawContent || '';

  msgLongPressTimer = setTimeout(() => {
    showMessageContextMenu(message, content);
  }, MSG_LONG_PRESS_DURATION);
}

function handleMouseMove(e) {
  if (!msgLongPressTimer) return;

  const moveX = Math.abs(e.clientX - longPressStartX);
  const moveY = Math.abs(e.clientY - longPressStartY);

  if (moveX > MOVE_THRESHOLD || moveY > MOVE_THRESHOLD) {
    clearTimeout(msgLongPressTimer);
    msgLongPressTimer = null;
  }
}

function handleMouseUp() {
  if (msgLongPressTimer) {
    clearTimeout(msgLongPressTimer);
    msgLongPressTimer = null;
  }
}

// ============================================
// 하위 호환성 (기존 호출 유지, 실제로는 아무 것도 안 함)
// ============================================

export function setupMessageLongPress(element, content) {
  // 이벤트 위임으로 전환됨 - 더 이상 개별 리스너 추가하지 않음
  // content는 dataset.rawContent에 저장되어 있음
  // initMessageLongPress()가 컨테이너에 리스너를 설정함
}

// ============================================
// 컨텍스트 메뉴 표시/닫기
// ============================================

export function showMessageContextMenu(element, content) {
  closeMessageContextMenu();

  contextMenuTarget = element;
  contextMenuContent = content;

  const hasTurn = element.dataset.turn;
  const isAssistant = element.classList.contains('assistant');

  const container = document.getElementById('messagesContainer');
  const allMessages = container ? container.querySelectorAll('.message[data-turn]') : [];
  const isLastMessage = allMessages.length > 0 && allMessages[allMessages.length - 1] === element;

  const showRollback = hasTurn && isAssistant && !isLastMessage;

  const modal = document.createElement('div');
  modal.id = 'messageContextMenu';
  modal.className = 'bottom-sheet-overlay';
  modal.innerHTML = `
    <div class="bottom-sheet-backdrop" data-action="close"></div>
    <div class="bottom-sheet message-context-sheet">
      <div class="bottom-sheet-handle"></div>
      <div class="bottom-sheet-body" style="padding: 8px 16px 24px 16px;">
        <div class="message-context-buttons">
          <button data-action="copy" class="message-context-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <span>복사하기</span>
          </button>
          ${showRollback ? `
          <button data-action="rollback" class="message-context-btn danger">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
            </svg>
            <span>되돌리기</span>
          </button>
          ` : ''}
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 위임
  modal.addEventListener('click', (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (action === 'close') closeMessageContextMenu();
    else if (action === 'copy') copyMessageContent();
    else if (action === 'rollback') showRollbackConfirm();
  });

  if (window.ModalManager) {
    window.ModalManager.register('messageContextMenu', closeMessageContextMenu, 'bottomSheet');
    window.ModalManager.push('messageContextMenu');
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
    });
  });
}

export function closeMessageContextMenu() {
  const modal = document.getElementById('messageContextMenu');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => {
      modal.remove();
    }, 300);

    if (window.ModalManager) {
      window.ModalManager.remove('messageContextMenu', true);
      window.ModalManager.unregister('messageContextMenu');
    }
  }
}

// ============================================
// 복사 기능
// ============================================

export function copyMessageContent() {
  if (!contextMenuContent) {
    closeMessageContextMenu();
    return;
  }

  let cleanContent = contextMenuContent;
  if (cleanContent.includes('<<<')) {
    cleanContent = cleanContent.split('<<<')[0].trim();
  }
  cleanContent = cleanContent.replace(/@silently\s*\{\{[\s\S]*?\}\}/g, '');
  cleanContent = cleanContent.replace(/@silently\s*\{(?!\{)[\s\S]*?\}/g, '');

  navigator.clipboard.writeText(cleanContent.trim())
    .then(() => showToast('복사되었습니다'))
    .catch(() => showToast('복사 실패', true));

  closeMessageContextMenu();
}

// ============================================
// 롤백 기능
// ============================================

export function showRollbackConfirm() {
  closeMessageContextMenu();

  if (!contextMenuTarget) return;

  const modal = document.createElement('div');
  modal.id = 'rollbackModal';
  modal.className = 'confirm-modal-overlay';
  modal.innerHTML = `
    <div class="confirm-modal-backdrop" data-action="close"></div>
    <div class="confirm-modal">
      <div class="confirm-modal-icon danger">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
          <path d="M3 3v5h5"></path>
        </svg>
      </div>
      <h3 class="confirm-modal-title">되돌리기</h3>
      <p class="confirm-modal-desc">
        해당 시점부터 다시 시작하시겠습니까?<br>
        <span class="confirm-modal-info">현재 상담은 복제되어 보존돼요</span>
      </p>
      <div class="confirm-modal-buttons">
        <button data-action="close" class="confirm-modal-btn cancel">취소</button>
        <button data-action="execute" class="confirm-modal-btn danger">되돌리기</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // 이벤트 위임
  modal.addEventListener('click', (e) => {
    const action = e.target.closest('[data-action]')?.dataset.action;
    if (action === 'close') closeRollbackModal();
    else if (action === 'execute') executeRollback();
  });

  if (window.ModalManager) {
    window.ModalManager.register('rollbackModal', closeRollbackModal, 'modal');
    window.ModalManager.push('rollbackModal');
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('open');
    });
  });
}

export function closeRollbackModal() {
  const modal = document.getElementById('rollbackModal');
  if (modal) {
    modal.classList.remove('open');
    setTimeout(() => {
      modal.remove();
    }, 200);

    if (window.ModalManager) {
      window.ModalManager.remove('rollbackModal', true);
      window.ModalManager.unregister('rollbackModal');
    }
  }
  contextMenuTarget = null;
  contextMenuContent = null;
}

// 백업 이름 생성 (상담 #MMDDHHmmss)
function generateBackupName() {
  const now = new Date();
  const code = [
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
    String(now.getHours()).padStart(2, '0'),
    String(now.getMinutes()).padStart(2, '0'),
    String(now.getSeconds()).padStart(2, '0')
  ].join('');
  return `상담 #${code}`;
}

export async function executeRollback() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  if (!contextMenuTarget || !roomId || !sessionId) {
    closeRollbackModal();
    return;
  }

  const targetTurn = parseInt(contextMenuTarget.dataset.turn);
  if (!targetTurn) {
    showToast('롤백할 수 없습니다.', true);
    closeRollbackModal();
    return;
  }

  const targetElement = contextMenuTarget;

  closeRollbackModal();
  showGlobalLoading();

  try {
    // 1. 먼저 현재 상담 복제 (백업)
    const duplicateResponse = await authFetch(CHAT_ENDPOINTS.duplicateSession, {
      body: { roomId, sessionId, customName: generateBackupName() }
    });

    if (!duplicateResponse.ok) {
      throw new Error('상담 백업 실패');
    }

    // 2. 롤백 진행
    const response = await authFetch(CHAT_ENDPOINTS.rollbackMessages, {
      body: { roomId, sessionId, targetTurn: targetTurn }
    });

    if (!response.ok) throw new Error('롤백 실패');

    const result = await response.json();

    const container = document.getElementById('messagesContainer');
    const messages = Array.from(container.querySelectorAll('.message'));
    let foundTarget = false;

    for (const msg of messages) {
      if (msg === targetElement) {
        foundTarget = true;
        continue;
      }
      if (foundTarget) {
        msg.remove();
      }
    }

    setCurrentTurnCount(result.newTurnCount);

    if (result.state && window.currentChatData) {
      if (!window.currentChatData.narrativeState) {
        window.currentChatData.narrativeState = { location: {}, time: {}, currentScene: {} };
      }
      if (result.state.location) window.currentChatData.narrativeState.location.current = result.state.location;
      if (result.state.time) window.currentChatData.narrativeState.time.description = result.state.time;
      if (result.state.situation) window.currentChatData.narrativeState.currentScene.description = result.state.situation;
    }

    updateFixedMetaBar();
    renderMemoryPanel('state');

    showToast('해당 시점으로 되돌렸습니다.');

  } catch (e) {
    console.error('Rollback error:', e);
    showToast('롤백 중 오류가 발생했습니다.', true);
  }

  hideGlobalLoading();
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
